/**
 * ProfilePage Component
 * 
 * User profile page - currently a placeholder.
 */

import React, { useState, useEffect } from 'react';
import { User, Shield, Star, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { ApiServices } from '../../services/ApiServices';
import { useAuth } from '../../context/AuthContext';

const ProfilePage = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        // Using getAuthUser to get fresh details including role/grade
        const response = await ApiServices.getCurrentUser();
        if (response && response.user) {
          setProfile(response.user);
        } else {
          // Fallback to auth context user if API fails or structure differs
          setProfile(user);
        }
      } catch (error) {
        console.error("Failed to load profile", error);
        setProfile(user);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [user]);

  useEffect(() => {
    ApiServices.trackAuditEvent('page_view', 'profile');
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto py-8 px-4">
      <h1 className="text-3xl font-bold mb-8">Мой Профиль</h1>

      <Card className="mb-8">
        <CardHeader>
          <div className="flex items-center gap-6">
            <div className="h-24 w-24 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="h-12 w-12 text-primary" />
            </div>
            <div>
              <CardTitle className="text-2xl">{profile?.username || 'Пользователь'}</CardTitle>
              <CardDescription className="flex items-center gap-2 mt-2">
                <Badge variant="outline" className="flex items-center gap-1">
                  <Shield className="h-3 w-3" />
                  {profile?.role || 'Пользователь'}
                </Badge>
                {profile?.grade && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    <Star className="h-3 w-3" />
                    {profile?.grade}
                  </Badge>
                )}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t pt-4">
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-1">Статус</h3>
                <div className={`text-base font-medium flex items-center ${profile?.is_active ? 'text-green-600' : 'text-muted-foreground'}`}>
                  {profile?.is_active ? 'Active' : 'Inactive'}
                </div>
              </div>
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-1">Дата регистрации</h3>
                <div className="text-base font-medium">
                  {profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : '—'}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default ProfilePage;